import { useEffect, useRef } from "react";

/**
 * Shared modal-dialog focus management (design 12.4: "keyboard operable, visible focus"; a modal
 * must not leak keyboard focus to the inert page behind it).
 *
 * Every modal in the SPA (the sources write forms, the alert rule/silence/channel/acknowledge
 * dialogs) already renders the correct roles — `role="dialog"`/`"alertdialog"`, `aria-modal`,
 * `aria-labelledby` — but roles alone do not move or contain focus. This one hook adds the four
 * behaviours a keyboard or screen-reader user needs, consistently, so the fix is durable rather
 * than re-implemented per dialog:
 *
 * 1. On open, focus moves INTO the dialog — the first enabled control, or the panel itself.
 * 2. Tab / Shift+Tab are trapped: focus cycles within the dialog and never reaches the page behind.
 * 3. Escape closes the dialog (the same affordance as the Cancel button / backdrop click).
 * 4. On close/unmount, focus RETURNS to whatever was focused when the dialog opened (the trigger),
 *    so the operator is not dumped at the top of the document.
 *
 * Usage: attach the returned ref to the dialog panel element (the one with `role="dialog"`), and
 * pass the close callback. The hook is purely additive — it does not render anything and leaves the
 * dialog's own markup untouched.
 *
 * It is intentionally dependency-free (no focus-trap library) and small: it queries the dialog's own
 * focusable descendants at the moment of each Tab, so it stays correct as the form's fields enable
 * and disable. `inert`/`aria-hidden` on the rest of the page is deliberately NOT toggled here — the
 * dialogs render in a fixed overlay and the trap already prevents keyboard escape; toggling global
 * inertness is left to a later pass if full-page screen-reader isolation is required (that needs
 * manual AT verification, see the task's honesty note).
 */

/** Selector for the elements that can hold keyboard focus inside a dialog. */
const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

export function useDialogFocus<T extends HTMLElement = HTMLDivElement>(
  onClose: () => void,
): React.RefObject<T> {
  // `useRef<T | null>(null)` is the correct runtime shape (null until mounted); we expose it as
  // `RefObject<T>` so a JSX `ref={panelRef}` type-checks under @types/react 18.3 (a `RefObject<T |
  // null>` is not assignable to a DOM element's `LegacyRef<T>`). `current` is still `T | null`.
  const dialogRef = useRef<T | null>(null) as React.RefObject<T>;
  // Keep the latest onClose without re-running the effect (which would re-grab focus each render).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;

    // Remember the trigger so focus can return to it on close.
    const previouslyFocused = document.activeElement;

    // Move focus into the dialog: the first focusable control, else the panel itself.
    const initial = focusableWithin(dialog)[0];
    if (initial !== undefined) {
      initial.focus();
    } else {
      // Make the panel itself programmatically focusable so the dialog still gets focus.
      if (!dialog.hasAttribute("tabindex")) dialog.setAttribute("tabindex", "-1");
      dialog.focus();
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = focusableWithin(dialog);
      if (focusable.length === 0) {
        // Nothing focusable but the panel — keep focus on it.
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !dialog.contains(active)) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      // Return focus to the trigger when the dialog closes/unmounts.
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, []);

  return dialogRef;
}
